import React from 'react'
import style from './style.module.css'
import { SuspenseLoading } from '@components/SuspenseLoading'
import Container from '@components/Container'
import Header from '@components/Header'
import TokenExpired from './components/TokenExpired'
import EmailVerified from './components/EmailVerified'
import EmailError from './components/EmailError'
import PageFooter from './components/PageFooter'

interface EmailVerifyProps {
  isLoading: boolean
  isEmailVerified: boolean
  error: string | null
  isTokenExpired?: boolean
  isTokenInvalid?: boolean
  isTooManyRequests?: boolean
}

export const EmailVerifyTemplate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <Container>
      <Header
        imgStyle={{ width: '35px', height: '35px' }}
        imgSrc="assets/app_logo.png"
      />
      <div className={style.contentWrapper} >
        {children}
        <ul className={style.contentList}>
            <li>
              <a
                className={style.contentLinks}
                href="https://apps.apple.com/nl/app/takeaway-com-courier/id1503301744?itsct=apps_box&itscg=30200"
              >
                <img
                  className={style.contentImages}
                  src="assets/apple-btn-en@2x.png"
                  alt="Download on the App Store"
                />
              </a>
              <a
                className={style.contentLinks}
                href="https://play.google.com/store/apps/details?id=com.takeaway.delivered4all&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"
              >
                <img
                  className={style.contentImages}
                  src="assets/google-play-badge@2x.png"
                  alt="Get it on Google Play"
                />
              </a>
            </li>
          </ul>
      </div>
      <PageFooter />
    </Container>
  )
}

export const EmailVerify: React.FC<EmailVerifyProps> = ({
  isLoading,
  isEmailVerified,
  error,
  isTokenExpired = false,
  isTokenInvalid = false,
  isTooManyRequests = false,
}) => {
  if (isLoading) {
    return <SuspenseLoading text="Verifying your email..." />
  }

  if (isTokenExpired) {
    return (
      <EmailVerifyTemplate>
        <TokenExpired />
      </EmailVerifyTemplate>
    )
  }

  if (isTokenInvalid) {
    return (
      <EmailVerifyTemplate>
        <EmailError />
      </EmailVerifyTemplate>
    )
  }

  if (isTooManyRequests) {
    return (
      <EmailVerifyTemplate>
        <EmailError />
      </EmailVerifyTemplate>
    )
  }

  if (error) {
    return (
      <EmailVerifyTemplate>
        <EmailError />
      </EmailVerifyTemplate>
    )
  }

  if (isEmailVerified) {
    return (
      <EmailVerifyTemplate>
        <EmailVerified/>
      </EmailVerifyTemplate>
    )
  }

  const isTestEnv = process.env.name === 'stage' || process.env.name === 'develop'

  if (!isTestEnv) {
    return null
  }

  return (
    <EmailVerifyTemplate>
      {/* TESTING ONLY - This section is for stage/develop environments */}
      <div className={style.contentWrapper}>
        <p style={{ 
          backgroundColor: '#fff3cd', 
          color: '#856404', 
          padding: '8px 12px', 
          borderRadius: '4px', 
          fontSize: '12px',
          fontWeight: 'bold',
          marginBottom: '16px'
        }}>
          🧪 TESTING PURPOSE ONLY - Please verify your test token using the "VERIFY TOKEN" input below.
        </p>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '14px',
          textAlign: 'left'
        }}>
          <p style={{ margin: '0 0 8px', fontWeight: 'bold' }}>Please test the following scenarios:</p>
          <ol style={{ margin: '0', paddingLeft: '20px' }}>
            <li style={{ marginBottom: '4px' }}>Real Token - Use a valid token to verify email</li>
            <li style={{ marginBottom: '4px' }}>Expired Token - Use an expired token to see the expiration message</li>
            <li style={{ marginBottom: '4px' }}>Invalid Token - Use an invalid token to see the error message</li>
          </ol>
        </div>
      </div>
    </EmailVerifyTemplate>
  )
}
